/**
 * Eine Anlage teilen — die Abschnitte in den Einstellungen (HM-160, ADR 0043).
 *
 * **Zwei Gestalten, je nachdem, wer hinsieht.** Der Besitzer sieht, mit wem er
 * teilt, ändert die Stufe, beendet eine Teilung und lädt per E-Mail ein — wie
 * einen Bewohner, mit demselben Link. Ein Gast sieht, wem die Anlage gehört und
 * was er darf, und kann seine Teilung selbst beenden; mehr nicht: Keine Stufe
 * vergibt Berechtigungen, auch nicht die eigene.
 *
 * **Die Stufen stehen in Worten neben der Wahl**, nicht in einem Tooltip: Wer
 * hier wählt, entscheidet, ob ein anderer Mensch Zähler und Messkonzept ändern
 * kann — und das soll er lesen, bevor er klickt.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Input, Select } from "@hausmeter/ui";
import { formatInstant } from "@hausmeter/i18n";
import type { ApiComponents } from "@hausmeter/sdk";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { displayName, initialsOf } from "../lib/format.js";
import { listRow, mutedText, rowLabel } from "../lib/list-styles.js";
import { useSharedSites, useSiteShares } from "../lib/queries.js";
import { navigate } from "../lib/router.js";
import { endedInvitation, shareAccessLabel } from "../lib/site-access.js";
import { PersonLine, SettingsSection } from "./settings-section.js";

type Access = ApiComponents["schemas"]["SiteShareAccess"];
type SiteShare = ApiComponents["schemas"]["SiteShare"];

const ACCESS_OPTIONS = [
  { value: "read", label: shareAccessLabel("read") },
  { value: "write", label: shareAccessLabel("write") },
] as const;

const row = { ...listRow, borderTop: "none", borderBottom: "1px solid var(--border-subtle)" } as const;

/**
 * Was der Besitzer sieht — der Abschnitt „Zugriff" des Entwurfs (HM-162): je
 * Person eine Zeile mit Initialen, der Besitzer zuerst, offene Einladungen in
 * derselben Liste („Einladung verschickt"), darunter abgesetzt das Formular.
 * Die Stufe bleibt eine Auswahl statt der Marke des Entwurfs: Sie zu ändern ist
 * eine Funktion, die es gibt.
 */
export function SiteSharesSection({ siteId, timeZone }: { readonly siteId: string; readonly timeZone: string }) {
  const client = useQueryClient();
  const shares = useSiteShares(siteId);
  const [email, setEmail] = useState("");
  const [access, setAccess] = useState<Access>("read");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const refresh = () => client.invalidateQueries({ queryKey: ["site-shares", siteId] });

  const invite = useMutation({
    mutationFn: async () => {
      const { data, error: failure } = await api.POST("/sites/{siteId}/shares/invitations", {
        params: { path: { siteId } },
        body: { email: email.trim(), access },
      });
      if (failure || !data) throw new Error(apiErrorMessage(failure) ?? "Die Einladung ließ sich nicht verschicken.");
      return data;
    },
    onSuccess: async () => {
      setEmail("");
      setAccess("read");
      setNotice("Einladung verschickt.");
      await refresh();
    },
    onError: (failure: Error) => setError(failure.message),
  });
  const change = useMutation({
    mutationFn: async (input: { shareId: string; access: Access }) => {
      const { error: failure } = await api.PATCH("/site-shares/{shareId}", {
        params: { path: { shareId: input.shareId } },
        body: { access: input.access },
      });
      if (failure) throw new Error(apiErrorMessage(failure) ?? "Die Berechtigung ließ sich nicht ändern.");
    },
    onSuccess: refresh,
    onError: (failure: Error) => setError(failure.message),
  });
  const end = useMutation({
    mutationFn: async (shareId: string) => {
      const { error: failure } = await api.DELETE("/site-shares/{shareId}", { params: { path: { shareId } } });
      if (failure) throw new Error(apiErrorMessage(failure) ?? "Die Teilung ließ sich nicht beenden.");
    },
    onSuccess: async () => {
      setNotice("Zugriff entfernt.");
      await refresh();
    },
    onError: (failure: Error) => setError(failure.message),
  });
  const revoke = useMutation({
    mutationFn: async (invitationId: string) => {
      const { error: failure } = await api.POST("/invitations/{invitationId}/revoke", { params: { path: { invitationId } } });
      if (failure) throw new Error(apiErrorMessage(failure) ?? "Die Einladung ließ sich nicht zurücknehmen.");
    },
    onSuccess: refresh,
    onError: (failure: Error) => setError(failure.message),
  });

  const data = shares.data;
  const mailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(email.trim());

  return (
    <SettingsSection title="Zugriff" subtitle="Wer diese Anlage außer dir sieht und ob er sie ändern darf. Änderungen hier gelten sofort.">
      {shares.isPending && <p style={{ ...mutedText, padding: "14px 20px" }}>Wird geladen …</p>}
      {shares.isError && <p style={{ ...mutedText, padding: "14px 20px" }}>Die Teilungen konnten nicht geladen werden.</p>}
      {data !== undefined && (
        <>
          <PersonLine name={displayName(data.owner)} detail={nameDetail(data.owner)} initials={initials(data.owner)}>
            <Badge tone="success">Besitzer</Badge>
          </PersonLine>
          {data.shares.map((share) => (
            <AccessLine
              key={share.id}
              share={share}
              busy={change.isPending || end.isPending}
              onChange={(next) => {
                setNotice(null);
                change.mutate({ shareId: share.id, access: next });
              }}
              onEnd={() => end.mutate(share.id)}
            />
          ))}
          {data.invitations.map((invitation) => {
            const ended = endedInvitation(invitation.status);
            return (
              <PersonLine
                key={invitation.id}
                name={invitation.email}
                detail={
                  ended !== null
                    ? `Einladung ${ended.hint}.`
                    : `Einladung verschickt · gültig bis ${formatInstant(new Date(invitation.expiresAt), timeZone, "de-DE", "date")}`
                }
                initials={initials({ firstName: null, lastName: null, email: invitation.email })}
              >
                {ended !== null ? (
                  <Badge>{ended.badge}</Badge>
                ) : (
                  <>
                    <Badge>{shareAccessLabel(invitation.access)}</Badge>
                    <Button variant="ghost" size="sm" disabled={revoke.isPending} onClick={() => revoke.mutate(invitation.id)}>
                      Zurücknehmen
                    </Button>
                  </>
                )}
              </PersonLine>
            );
          })}
        </>
      )}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!mailOk) return;
          setError(null);
          setNotice(null);
          invite.mutate();
        }}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          padding: "16px 20px",
          background: "var(--surface-sunken)",
          borderRadius: "0 0 var(--radius-lg) var(--radius-lg)",
        }}
      >
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-strong)" }}>Person einladen</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-start" }}>
          <Input
            label="E-Mail-Adresse"
            labelHidden
            type="email"
            placeholder="name@beispiel.de"
            value={email}
            style={{ flex: "2 1 220px" }}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Select
            label="Berechtigung"
            labelHidden
            options={ACCESS_OPTIONS}
            value={access}
            style={{ flex: "1 1 180px" }}
            onChange={(event) => setAccess(event.target.value as Access)}
          />
          <Button type="submit" disabled={!mailOk || invite.isPending}>
            {invite.isPending ? "Wird verschickt …" : "Einladen"}
          </Button>
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>
          {access === "write"
            ? "Sieht alles und ändert zusätzlich Stammdaten, Zähler, Messkonzept und Geräte."
            : "Sieht alles, was du siehst, und ändert nichts."}{" "}
          Löschen, Archivieren und Teilen bleiben bei dir. Die Person meldet sich mit genau dieser Adresse an.
        </div>
        {notice !== null && <div role="status" style={{ fontSize: 12.5, fontWeight: 600, color: "var(--hm-green-700)" }}>{notice}</div>}
        {error !== null && <Alert tone="danger">{error}</Alert>}
      </form>
    </SettingsSection>
  );
}

/** Eine Teilung im Abschnitt „Zugriff": Stufe als Auswahl, Entfernen mit Rückfrage. */
function AccessLine({
  share,
  busy,
  onChange,
  onEnd,
}: {
  readonly share: SiteShare;
  readonly busy: boolean;
  readonly onChange: (access: Access) => void;
  readonly onEnd: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <PersonLine name={displayName(share.user)} detail={nameDetail(share.user)} initials={initials(share.user)}>
      <Select
        label={`Berechtigung von ${displayName(share.user)}`}
        labelHidden
        size="sm"
        options={ACCESS_OPTIONS}
        value={share.access}
        disabled={busy}
        onChange={(event) => onChange(event.target.value as Access)}
      />
      {confirming ? (
        <>
          <Button variant="danger" size="sm" disabled={busy} onClick={onEnd}>
            Entfernen
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
            Abbrechen
          </Button>
        </>
      ) : (
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirming(true)}>
          Entfernen …
        </Button>
      )}
    </PersonLine>
  );
}

type Person = ApiComponents["schemas"]["SharePerson"] | { firstName: null; lastName: null; email: string };

/** Die Adresse als zweite Zeile — nur, wenn die erste ein Name ist. */
function nameDetail(person: Person): string | undefined {
  return displayName(person) === person.email ? undefined : person.email;
}

/** Initialen aus dem Namen, sonst der erste Buchstabe der Adresse — im Kreis, nicht als Aussage. */
function initials(person: Person): string {
  return initialsOf(person) ?? person.email.slice(0, 1).toUpperCase();
}

export function PersonCell({ person }: { readonly person: ApiComponents["schemas"]["SharePerson"] }) {
  const name = displayName(person);
  return (
    <span style={{ minWidth: "min(220px, 100%)", display: "flex", flexDirection: "column" }}>
      <span style={{ ...rowLabel, overflowWrap: "anywhere" }}>{name}</span>
      {name !== person.email && <span style={{ ...mutedText, fontSize: 12, overflowWrap: "anywhere" }}>{person.email}</span>}
    </span>
  );
}

/** Eine Teilung: wer, die Stufe als Auswahl, und Beenden mit Rückfrage. */
export function ShareRow({
  share,
  busy,
  onChange,
  onEnd,
}: {
  readonly share: SiteShare;
  readonly busy: boolean;
  readonly onChange: (access: Access) => void;
  readonly onEnd: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <li style={row}>
      <PersonCell person={share.user} />
      <span style={{ marginLeft: "auto", display: "flex", gap: "var(--sp-2)", alignItems: "center", flexWrap: "wrap" }}>
        <Select
          label={`Berechtigung von ${displayName(share.user)}`}
          labelHidden
          size="sm"
          options={ACCESS_OPTIONS}
          value={share.access}
          disabled={busy}
          onChange={(e) => onChange(e.target.value as Access)}
        />
        {confirming ? (
          <>
            <Button variant="danger" size="sm" disabled={busy} onClick={onEnd}>
              Nicht mehr teilen
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
              Abbrechen
            </Button>
          </>
        ) : (
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirming(true)}>
            Beenden …
          </Button>
        )}
      </span>
    </li>
  );
}

/** Was ein Gast sieht: wem die Anlage gehört, was er darf, und wie er geht. */
export function SharedWithMeSection({ siteId }: { readonly siteId: string }) {
  const client = useQueryClient();
  const shared = useSharedSites();
  const entry = shared.data?.sites.find((candidate) => candidate.site.id === siteId);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const leave = useMutation({
    mutationFn: async (shareId: string) => {
      const { error: failure } = await api.DELETE("/site-shares/{shareId}", { params: { path: { shareId } } });
      if (failure) throw new Error(apiErrorMessage(failure) ?? "Die Teilung ließ sich nicht beenden.");
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["me"] });
      navigate({ kind: "start" }, { replace: true });
    },
    onError: (failure: Error) => setError(failure.message),
  });

  if (entry === undefined) return null;

  return (
    <SettingsSection title="Zugriff" subtitle="Diese Anlage gehört nicht dir — sie ist mit dir geteilt.">
      <PersonLine name={displayName(entry.owner)} detail={nameDetail(entry.owner)} initials={initials(entry.owner)}>
        <Badge tone="success">Besitzer</Badge>
      </PersonLine>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "16px 20px" }}>
        <p style={{ ...mutedText, lineHeight: 1.55 }}>
          Deine Berechtigung: <strong>{shareAccessLabel(entry.access)}</strong>.{" "}
          {entry.access === "write"
            ? "Du kannst Stammdaten, Zähler, Messkonzept und Geräte ändern."
            : "Du siehst alles, änderst aber nichts."}{" "}
          Löschen, Archivieren und Teilen bleiben beim Besitzer; eine andere Berechtigung vergibt nur er.
        </p>
        {error !== null && <Alert tone="danger">{error}</Alert>}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {confirming ? (
            <>
              <Button variant="danger" size="sm" disabled={leave.isPending} onClick={() => leave.mutate(entry.shareId)}>
                {entry.site.name} nicht mehr sehen
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                Abbrechen
              </Button>
            </>
          ) : (
            <Button variant="secondary" size="sm" onClick={() => setConfirming(true)}>
              Nicht mehr sehen …
            </Button>
          )}
        </div>
      </div>
    </SettingsSection>
  );
}
